import React from 'react';
import { useLanguage } from '../contexts/LanguageContext';
import { translations } from '../translations';
import { ArrowRight, Home } from 'lucide-react';

const Bestellung = () => {
  const { language, setLanguage } = useLanguage();
  const t = translations[language];

  const orderOptions = [
    {
      name: 'Wolt',
      url: 'https://order.site/cobarci/tr/deu/berlin/restaurant/corbaci-sf?no_universal_links=true',
      icon: '🛵'
    },
    {
      name: 'Uber Eats',
      url: 'https://www.ubereats.com/de/store/corbaci-authentische-turkische-kuche/Z4L9pQSxVnyHd6TEiOAjOQ',
      icon: '🚗'
    },
    {
      name: 'Lieferando',
      url: 'https://www.lieferando.de/speisekarte/corbaci-10627',
      icon: '🍕'
    },
    {
      name: 'WhatsApp',
      url: 'https://wa.me/493089542916',
      icon: '💬'
    },
    {
      name: language === 'de' ? 'Abholung' : language === 'en' ? 'Pickup' : 'Gel Al',
      url: 'https://order.site/cobarci/tr/deu/berlin/restaurant/corbaci-sf?no_universal_links=true',
      icon: '🛍️'
    }
  ];

  return (
    <div className="min-h-screen bg-black">
      {/* Header Navigation */}
      <div className="fixed top-0 left-0 right-0 z-50 bg-black/95 backdrop-blur-md border-b border-white/10">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <div className="flex items-center justify-between">
            {/* Logo & Home Button */}
            <div className="flex items-center gap-3">
              <a href="/" className="flex items-center gap-3 hover:opacity-80 transition-opacity duration-200">
                <img 
                  src="/images/logo.png" 
                  alt="Corbaci" 
                  className="h-10 w-auto"
                />
              </a>
              
              <a 
                href="/" 
                className="flex items-center gap-2 bg-[#d4af37] text-black px-4 py-2 rounded-md font-semibold hover:bg-[#f0c24d] transition-all duration-200 transform hover:scale-105"
              >
                <Home className="w-4 h-4" />
                <span className="hidden sm:inline">{t.nav_home}</span>
              </a>
            </div>

            {/* Language Switcher */}
            <div className="flex items-center gap-2 bg-white/10 rounded-md px-2 py-1">
              <button 
                onClick={() => setLanguage('de')} 
                className={`px-2 py-1 text-xs font-medium rounded transition-colors duration-200 ${language === 'de' ? 'bg-[#d4af37] text-black' : 'text-white/70 hover:text-white'}`}
              >
                DE
              </button>
              <button 
                onClick={() => setLanguage('en')} 
                className={`px-2 py-1 text-xs font-medium rounded transition-colors duration-200 ${language === 'en' ? 'bg-[#d4af37] text-black' : 'text-white/70 hover:text-white'}`}
              >
                EN
              </button>
              <button 
                onClick={() => setLanguage('tr')} 
                className={`px-2 py-1 text-xs font-medium rounded transition-colors duration-200 ${language === 'tr' ? 'bg-[#d4af37] text-black' : 'text-white/70 hover:text-white'}`}
              >
                TR
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="flex items-center justify-center px-4 py-32">
        <div className="max-w-2xl w-full">
          {/* Title */}
          <div className="text-center mb-12">
            <h1 className="text-4xl md:text-6xl font-bold text-white mb-4" style={{ fontFamily: "'Crimson Text', serif" }}>
              {t.order_title}
            </h1>
            <p className="text-white/70 text-lg md:text-xl">
              {t.order_subtitle}
            </p>
          </div>

          {/* Order Buttons */}
          <div className="space-y-4">
            {orderOptions.map((option, index) => (
              <a
                key={index}
                href={option.url}
                target="_blank"
                rel="noopener noreferrer"
                className="group block w-full bg-white/5 backdrop-blur-sm hover:bg-white/10 border border-white/10 hover:border-[#d4af37]/50 rounded-2xl p-6 transform hover:scale-105 transition-all duration-300 shadow-lg hover:shadow-2xl"
              >
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-4">
                    <span className="text-5xl">{option.icon}</span>
                    <div>
                      <h3 className="text-2xl font-bold text-white group-hover:text-[#d4af37] transition-colors duration-200">
                        {option.name}
                      </h3>
                      <p className="text-white/60 text-sm">
                        {language === 'de' ? 'Jetzt bestellen' : language === 'en' ? 'Order now' : 'Sipariş ver'}
                      </p>
                    </div>
                  </div>
                  <ArrowRight className="w-8 h-8 text-[#d4af37] group-hover:translate-x-2 transition-transform duration-200" />
                </div>
              </a>
            ))}
          </div>

          {/* Contact Info */}
          <div className="mt-12">
            <div className="bg-white/5 backdrop-blur-sm rounded-2xl p-8 border border-white/10 text-center">
              <p className="text-white/70 mb-3">
                {language === 'de' ? 'Oder rufen Sie uns direkt an:' : language === 'en' ? 'Or call us directly:' : 'Veya bizi arayın:'}
              </p>
              <a 
                href="tel:+493089542916" 
                className="inline-block text-[#d4af37] text-3xl font-bold hover:text-[#f0c24d] transition-colors duration-200 mb-4"
              >
                +49 30 8954 2916
              </a>
              <div className="text-white/60 text-sm space-y-1">
                <p>Kaiser-Friedrich-Straße 49</p>
                <p>10627 Berlin, Charlottenburg</p>
                <p className="text-[#d4af37] font-semibold mt-2">
                  {t.hours_value}
                </p>
              </div>
            </div>
          </div>

          {/* Footer Link */}
          <div className="mt-8 text-center">
            <a 
              href="/" 
              className="inline-flex items-center gap-2 text-white/60 hover:text-[#d4af37] transition-colors duration-200"
            >
              <Home className="w-4 h-4" />
              <span>{language === 'de' ? 'Zurück zur Startseite' : language === 'en' ? 'Back to home' : 'Ana sayfaya dön'}</span>
            </a>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Bestellung;
